import { Box, Typography, alpha } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { Img } from '@/components/blocks/MaterialImage';
import { useResizeObserverByElementId } from 'integration/hooks/useResizeObserverByElementId';
import { InterestsPreferenceChildren } from 'integration/generated/transactions/data-contracts';

export const INTERESTS_BASE_CDN_URL =
	'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/intereses/dp-categorias';
export const HOBBIES_BASE_CDN_URL = `${INTERESTS_BASE_CDN_URL}/hobbies`;

interface AccountInterestProps {
	interests: InterestsPreferenceChildren[];
	selectedUserInterests: Array<number>;
	containerId?: string;
	variant?: 'vertical' | 'horizontal';
}

export const getInterestImage = (name: string, isHobby: boolean = false) => {
	const normalized = name
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/\s+/g, '-')
		.replace(/[^a-z0-9\-]/g, '');

	return `${isHobby ? HOBBIES_BASE_CDN_URL : INTERESTS_BASE_CDN_URL}/${normalized}.${
		isHobby ? 'webp' : 'png'
	}`;
};

export const AccountInterest: React.FC<AccountInterestProps> = ({
	interests,
	selectedUserInterests,
	containerId = 'account-interests',
	variant = 'vertical',
}) => {
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const { width: widthInterests } = useResizeObserverByElementId(containerId);

	const [isDragging, setIsDragging] = useState(false);
	const startXRef = useRef(0);
	const scrollLeftRef = useRef(0);

	const sortedInterests = useMemo(
		() =>
			[...interests].sort((a, b) => {
				const aSelected = selectedUserInterests.includes(a.id);
				const bSelected = selectedUserInterests.includes(b.id);

				if (aSelected && !bSelected) return -1;
				if (!aSelected && bSelected) return 1;
				return 0;
			}),
		[interests, selectedUserInterests]
	);

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!scrollRef.current) return;
		setIsDragging(true);
		startXRef.current = e.pageX - scrollRef.current.offsetLeft;
		scrollLeftRef.current = scrollRef.current.scrollLeft;
	};

	const handleMouseLeave = () => {
		setIsDragging(false);
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!isDragging || !scrollRef.current) return;
		e.preventDefault();
		const x = e.pageX - scrollRef.current.offsetLeft;
		const walk = x - startXRef.current;
		scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
	};

	return (
		<Box
			id={containerId}
			ref={scrollRef}
			onMouseDown={handleMouseDown}
			onMouseLeave={handleMouseLeave}
			onMouseUp={handleMouseUp}
			onMouseMove={handleMouseMove}
			sx={{
				display: 'flex',
				flexDirection: 'row',
				gap: '6px',
				width: '100%',
				maxWidth: `${widthInterests}px`,
				overflowX: 'auto',
				cursor: isDragging ? 'grabbing' : 'grab',
				scrollbarWidth: '0px',
				'&:not(:hover)::-webkit-scrollbar': {
					height: '8px',
					background: 'transparent',
				},
				'&:not(:hover)::-webkit-scrollbar-track': {
					background: 'transparent',
				},
				'&:not(:hover)::-webkit-scrollbar-thumb': {
					backgroundColor: 'transparent',
				},
				'&:hover::-webkit-scrollbar': {
					height: '8px',
				},
				'&:hover::-webkit-scrollbar-track': {
					background: '#EDEDED',
				},
				'&:hover::-webkit-scrollbar-thumb': {
					backgroundColor: '#5D5D5D',
					borderRadius: '10px',
					border: '2px solid transparent',
				},
				'&:hover::-webkit-scrollbar-thumb:hover': {
					backgroundColor: '#555',
				},
				'&::-webkit-scrollbar-button': {
					display: 'none',
				},
				pb: 'var(--spacing-2)',
			}}
		>
			{sortedInterests.map((interest) => {
				const isSelected = selectedUserInterests.includes(interest.id);

				if (variant === 'horizontal') {
					return (
						<Box
							key={interest.id}
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								gap: '12px',
								userSelect: 'none',
								backgroundColor: isSelected
									? (t) => alpha(t.palette.primary.main, 0.08)
									: '#FFFFFF',
								border: '1px solid',
								borderColor: isSelected ? 'primary.main' : '#E0E0E0',
								borderRadius: '4px',
								padding: '8px 12px',
								minWidth: '200px',
								maxWidth: '220px',
								height: '60px',
								flexShrink: 0,
							}}
						>
							<Img
								src={getInterestImage(interest.name, true)}
								alt={interest.name}
								draggable={false}
								sx={{
									width: '32px',
									minWidth: '32px',
									height: '32px',
									objectFit: 'contain',
								}}
							/>
							<Typography
								sx={{
									fontSize: '14px',
									color: isSelected ? 'primary.main' : '#2A2A2A',
									fontWeight: isSelected ? 600 : 400,
									lineHeight: 1.2,
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{interest.name}
							</Typography>
						</Box>
					);
				}

				return (
					<Box
						key={interest.id}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '5px',
							userSelect: 'none',
							backgroundColor: isSelected
								? (t) => alpha(t.palette.primary.main, 0.08)
								: 'transparent',
							border: isSelected ? '1px solid' : 'none',
							borderColor: isSelected ? 'primary.main' : 'transparent',
						}}
					>
						<Img
							src={getInterestImage(interest.name)}
							alt={interest.name}
							draggable={false}
							sx={{
								width: '134px',
								minWidth: '134px',
								minHeight: '134px',
								objectFit: 'cover',
							}}
						/>
						<Typography
							sx={{
								fontSize: '14px',
								textAlign: 'center',
								p: '0px 0px 5px 0px',
								color: selectedUserInterests.includes(interest.id) ? 'primary.main' : '#2A2A2A',
							}}
						>
							{interest.name}
						</Typography>
					</Box>
				);
			})}
		</Box>
	);
};
